import { httpClient } from "@/libs/axios";
import {
  loginSchema,
  logoutSchema,
  refreshTokenSchema,
  registerClubSchema,
  registerPlayerSchema,
  onboardClubSchema,
  updatePasswordSchema,
  updateProfileSchema,
  googleLoginSchema,
  magicLinkRequestSchema,
  magicLinkVerifySchema,
  TLoginRequest,
  TLogoutRequest,
  TRefreshTokenRequest,
  TRegisterClubRequest,
  TRegisterPlayerRequest,
  TOnboardClubRequest,
  TUpdatePasswordRequest,
  TUpdateProfileRequest,
  TGoogleLoginRequest,
  TMagicLinkRequest,
  TMagicLinkVerifyRequest,
} from "./schema";
import {
  TLoginResponse,
  TLogoutResponse,
  TMeResponse,
  TRefreshTokenResponse,
  TRegisterClubResponse,
  TOnboardClubResponse,
  TUpdatePasswordResponse,
  TUpdateProfileResponse,
  TGoogleLoginResponse,
  TMagicLinkRequestResponse,
  TMagicLinkVerifyResponse,
} from "./type";

export const login = async (credentials: TLoginRequest): Promise<TLoginResponse> => {
  const validationResult = loginSchema.safeParse(credentials);
  if (!validationResult.success) {
    throw new Error("Invalid login credentials");
  }

  const { data } = await httpClient.post<TLoginResponse>("/auth/login", credentials);
  return data;
};

export const loginWithGoogle = async (
  request: TGoogleLoginRequest
): Promise<TGoogleLoginResponse> => {
  const validationResult = googleLoginSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid Google login request");
  }

  const { data } = await httpClient.post<TGoogleLoginResponse>("/auth/google", request);
  return data;
};

export const requestMagicLink = async (
  request: TMagicLinkRequest
): Promise<TMagicLinkRequestResponse> => {
  const validationResult = magicLinkRequestSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid email address");
  }

  const { data } = await httpClient.post<TMagicLinkRequestResponse>(
    "/auth/magic-link/request",
    request
  );
  return data;
};

export const verifyMagicLink = async (
  request: TMagicLinkVerifyRequest
): Promise<TMagicLinkVerifyResponse> => {
  const validationResult = magicLinkVerifySchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid magic link token");
  }

  const { data } = await httpClient.post<TMagicLinkVerifyResponse>(
    "/auth/magic-link/verify",
    request
  );
  return data;
};

export const logout = async (request: TLogoutRequest): Promise<TLogoutResponse> => {
  const validationResult = logoutSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid logout request");
  }

  const { data } = await httpClient.post<TLogoutResponse>("/auth/logout", request);
  return data;
};

export const refreshToken = async (
  request: TRefreshTokenRequest
): Promise<TRefreshTokenResponse> => {
  const validationResult = refreshTokenSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid refresh token request");
  }

  const { data } = await httpClient.post<TRefreshTokenResponse>("/auth/refresh", request);
  return data;
};

export const registerClub = async (
  request: TRegisterClubRequest
): Promise<TRegisterClubResponse> => {
  const validationResult = registerClubSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid registration data");
  }

  const { data } = await httpClient.post<TRegisterClubResponse>(
    "/auth/register-club",
    request
  );
  return data;
};

export const registerPlayer = async (
  request: TRegisterPlayerRequest
): Promise<TLoginResponse> => {
  const validationResult = registerPlayerSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid registration data");
  }

  const { data } = await httpClient.post<TLoginResponse>(
    "/auth/register-player",
    request
  );
  return data;
};

export const onboardClub = async (
  request: TOnboardClubRequest
): Promise<TOnboardClubResponse> => {
  const validationResult = onboardClubSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid club information");
  }

  const { data } = await httpClient.post<TOnboardClubResponse>(
    "/auth/onboard-club",
    request
  );
  return data;
};

export const getMe = async (): Promise<TMeResponse> => {
  const { data } = await httpClient.get<TMeResponse>("/auth/me");
  return data;
};

export const updateProfile = async (
  request: TUpdateProfileRequest
): Promise<TUpdateProfileResponse> => {
  const validationResult = updateProfileSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid profile data");
  }

  const { data } = await httpClient.put<TUpdateProfileResponse>(
    "/auth/profile",
    request
  );
  return data;
};

export const updatePassword = async (
  request: TUpdatePasswordRequest
): Promise<TUpdatePasswordResponse> => {
  const validationResult = updatePasswordSchema.safeParse(request);
  if (!validationResult.success) {
    throw new Error("Invalid password data");
  }

  // confirmPassword is client-side only; the API expects current/new only.
  const { data } = await httpClient.put<TUpdatePasswordResponse>(
    "/auth/password",
    {
      currentPassword: request.currentPassword,
      newPassword: request.newPassword,
    }
  );
  return data;
};
